/**
 * Supported HTTP methods for requests.
 */
export type HttpMethod = 'post' | 'get' | 'put' | 'delete' | 'patch'

/**
 * Factory type for creating Error instances with an optional message.
 *
 * @param msg - Optional message to be included in the Error instance.
 * @returns An instance of Error.
 */
export type ErrorFactory = (msg?: string | null) => Error

/**
 * Enum for parameter naming strategies.
 */
export enum NamingStrategy {
  /** Snake case naming strategy (e.g., snake_case) */
  snakeCase = 'snake_case',
  /** Camel case naming strategy (e.g., camelCase) */
  camelCase = 'camelCase'
}

/**
 * Type for parameter naming strategies.
 */
export type ParamsNamingStrategy = NamingStrategy.snakeCase | NamingStrategy.camelCase

// Interface para requisições HTTP
/**
 * Represents the configuration for an API request instance.
 *
 * @property url - The endpoint URL for the API request.
 * @property method - The HTTP method to be used (e.g., GET, POST).
 * @property body - Optional payload to be sent with the request.
 * @property headers - Optional partial set of headers for the request.
 * @property params - Optional partial set of query parameters for the request.
 * @property withCredentials - Indicates whether credentials such as cookies should be sent with the request.
 * @property paramsNamingStrategy - Optional strategy for naming query parameters.
 * @property onUploadProgress - Optional callback function to track upload progress, receives the progress percentage.
 * @property skipAuthRedirect - If true, skip automatic redirect to login on 401 errors. Useful for login/authentication endpoints.
 * @property signal - Optional AbortSignal to cancel the request.
 */
export type ApiInstanceRequest = {
  url: string
  method: HttpMethod
  body?: unknown
  headers?: Partial<unknown>
  params?: Partial<unknown>
  withCredentials?: boolean
  paramsNamingStrategy?: ParamsNamingStrategy
  onUploadProgress?: (progressPercent: number) => void
  skipAuthRedirect?: boolean
  signal?: AbortSignal
  responseType?: 'blob' | 'arraybuffer' | 'text' | 'json'
}

/**
 * Represents the standard response from the API.
 *
 * @template T - The type of the response data.
 * @property success - Indicates whether the API request was successful.
 * @property message - Optional message returned by the API.
 * @property data - The response data, or null if not present.
 */
export interface ApiInstanceResponse<T> {
  success: boolean
  message?: string | null
  data: T | null
}

// Interface para retorno completo da API, incluindo status e headers
/**
 * Represents the return type of an API instance call.
 *
 * @template T - The type of the response body.
 * @property statusCode - The HTTP status code returned by the API.
 * @property headers - Optional HTTP headers returned by the API. Each header can be a string, array of strings, number, boolean, or null.
 * @property body - The response body, typed as `ApiInstanceResponse<T>`.
 */
export interface ApiInstanceReturn<T> {
  statusCode: number
  headers?: Record<string, string | string[] | number | boolean | null>
  body: ApiInstanceResponse<T>
  correlationId?: string
}
